/* =========================================================================
   XP-bar math — the pure half of the progression UI (PLAN-progression.md §5).
   Kept apart from the DOM so the "does the bar sit where it should" questions
   are unit tests, not eyeballing an animation.
   ========================================================================= */

/** Bar fill for `into / size`, 0-100 and clamped. A level with no known size
    (server omitted xpToNextLevel) reads as an empty bar, never NaN. PURE. */
export function xpPct(xpIntoLevel: number, xpToNextLevel: number): number {
  if (!Number.isFinite(xpIntoLevel) || !Number.isFinite(xpToNextLevel)) return 0;
  if (xpToNextLevel <= 0) return 0;
  const pct = (xpIntoLevel / xpToNextLevel) * 100;
  return Math.max(0, Math.min(100, Math.round(pct * 10) / 10));
}

/** "1,240" — thousands-separated XP, always an integer. PURE. */
export function fmtXp(xp: number): string {
  const n = Number.isFinite(xp) ? Math.round(xp) : 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export interface TallyOpts {
  xpAwarded: number;
  xpIntoLevel: number;
  xpToNextLevel: number;
  level: number;
  leveledUp: boolean;
}

/** Where the bar animates FROM and TO for a round-end award.

    The caller only knows the POST-award state, so the pre-award position is
    derived:
      • no level-up — the bar simply rewinds by the award:
        from = xpIntoLevel − xpAwarded (clamped at 0).
      • level-up — the award crossed a boundary, so the bar fills to 100%,
        resets, and fills again. The XP that finished the previous level is
        exactly `xpAwarded − xpIntoLevel`; the previous level's SIZE is not sent,
        so the current level's size stands in for it (clamped so `from` can never
        go negative or exceed the bar).
    PURE. */
export function tallyBar(opts: TallyOpts): { fromPct: number; toPct: number; wraps: boolean } {
  const awarded = Math.max(0, Number.isFinite(opts.xpAwarded) ? opts.xpAwarded : 0);
  const into = Math.max(0, Number.isFinite(opts.xpIntoLevel) ? opts.xpIntoLevel : 0);
  const size = Math.max(0, Number.isFinite(opts.xpToNextLevel) ? opts.xpToNextLevel : 0);
  const toPct = xpPct(into, size);

  if (!opts.leveledUp) {
    return { fromPct: xpPct(Math.max(0, into - awarded), size), toPct, wraps: false };
  }
  // Level-up: `needed` finished the previous level; estimate that level's size.
  const needed = Math.max(0, awarded - into);
  const prevSize = Math.max(needed, size);
  const fromPct = prevSize <= 0 ? 0 : xpPct(Math.max(0, prevSize - needed), prevSize);
  return { fromPct, toPct, wraps: true };
}

/** Ease-out for count-ups and bar fills (no library, App Store privacy rule). */
export function easeOutCubic(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - c, 3);
}

/** The XP number shown at `t` (0..1) of the count-up, eased and rounded. PURE. */
export function countUpValue(target: number, t: number): number {
  const end = Number.isFinite(target) ? target : 0;
  return Math.round(end * easeOutCubic(t));
}
